import { UpdateOrg } from "@garther/interfaces";
import { Box, Button, CustomEditor, ImageInput, Input } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import "ckeditor5/ckeditor5.css";
import { useState } from "react";

import { RawLayout } from "@/components";
import { publicApiClient } from "@/libs/api";
import { orgApiClient } from "@/libs/api/org-client";

export const Route = createFileRoute("/org/$orgId/edit/")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const org = await orgApiClient.getFullDetail(params.orgId);
    if (!org) {
      throw new Error("Organization not found");
    }
    return { data: { org } };
  }
});

function RouteComponent() {
  const { t } = useI18n();
  const { data } = useLoaderData({ from: "/org/$orgId/edit/" });
  const { org } = data;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<UpdateOrg>({
    defaultValues: {
      name: org.name,
      tagline: org.tagline,
      description: org.description,
      mission: org.mission,
      avatar: org.avatar,
      cover: org.cover,
      contact: {
        email: org.contact?.email || null,
        phone: org.contact?.phone || null,
        website: org.contact?.website || null,
        instagram: org.contact?.instagram || null,
        facebook: org.contact?.facebook || null,
        twitter: org.contact?.twitter || null,
        youtube: org.contact?.youtube || null
      }
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Clean up the data - remove null/empty values
        const updateData: UpdateOrg = {};

        if (value.name && value.name.trim() !== org.name) {
          updateData.name = value.name.trim();
        }
        if (value.tagline && value.tagline.trim() !== org.tagline) {
          updateData.tagline = value.tagline.trim();
        }
        if (value.description && value.description.trim() !== org.description) {
          updateData.description = value.description.trim();
        }
        if (value.mission && value.mission.trim() !== org.mission) {
          updateData.mission = value.mission.trim();
        }
        if (value.avatar && value.avatar.trim() !== org.avatar) {
          updateData.avatar = value.avatar.trim();
        }
        if (value.cover && value.cover.trim() !== org.cover) {
          updateData.cover = value.cover.trim();
        }

        // Handle contact info
        if (value.contact) {
          const contactChanges: any = {};
          let hasContactChanges = false;

          if (value.contact.email !== org.contact?.email) {
            contactChanges.email = value.contact.email || null;
            hasContactChanges = true;
          }
          if (value.contact.phone !== org.contact?.phone) {
            contactChanges.phone = value.contact.phone || null;
            hasContactChanges = true;
          }
          if (value.contact.website !== org.contact?.website) {
            contactChanges.website = value.contact.website || null;
            hasContactChanges = true;
          }
          if (value.contact.instagram !== org.contact?.instagram) {
            contactChanges.instagram = value.contact.instagram || null;
            hasContactChanges = true;
          }
          if (value.contact.facebook !== org.contact?.facebook) {
            contactChanges.facebook = value.contact.facebook || null;
            hasContactChanges = true;
          }
          if (value.contact.twitter !== org.contact?.twitter) {
            contactChanges.twitter = value.contact.twitter || null;
            hasContactChanges = true;
          }
          if (value.contact.youtube !== org.contact?.youtube) {
            contactChanges.youtube = value.contact.youtube || null;
            hasContactChanges = true;
          }

          if (hasContactChanges) {
            updateData.contact = contactChanges;
          }
        }

        // Only update if there are changes
        if (Object.keys(updateData).length > 0) {
          await orgApiClient.update(org.id, updateData);
          toast.success(t("org.edit.success"));
          navigate({
            to: "/org/$orgId",
            params: { orgId: org.id }
          });
        } else {
          toast.info(t("org.edit.noChanges"));
        }
      } catch (error) {
        console.error("Error updating organization:", error);
        toast.error(t("org.edit.error"));
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  return (
    <RawLayout>
      <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
        <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto">
          <div className="px-4 md:px-6">
            <p className="text-responsive-xl font-normal text-text-primary mb-2">{t("org.edit.title")}</p>
            <p className="text-responsive-base text-text-secondary">
              {t("org.edit.subtitle", { orgName: org.name })}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="px-4 md:px-6 py-2">
              <p className="text-text-secondary text-responsive-lg font-normal">{t("org.edit.basicInfo")}</p>
            </div>
            <Box className="space-y-6 mb-6">
              {/* Basic Information Section */}
              <div className="space-y-4">
                {/* Avatar */}
                <form.Field
                  name="avatar"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                        return t("org.edit.errors.invalidUrl");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <ImageInput
                      value={field.state.value}
                      onChange={field.handleChange}
                      uploadFn={publicApiClient.uploadImage}
                      label={t("org.edit.avatar")}
                      required={true}
                      previewSize="w-32 h-32"
                      disabled={isSubmitting}
                      toastSuccess={toast.success}
                      toastError={toast.error}
                      successMessage={t("org.edit.avatarUploadSuccess")}
                      errorMessage={t("org.edit.avatarUploadError")}
                      invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                      fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                      alt="Avatar preview"
                    />
                  )}
                </form.Field>

                {/* Cover */}
                <form.Field
                  name="cover"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                        return t("org.edit.errors.invalidUrl");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <ImageInput
                      value={field.state.value}
                      onChange={field.handleChange}
                      uploadFn={publicApiClient.uploadImage}
                      label={t("org.edit.cover")}
                      required={true}
                      previewSize="w-full max-w-md h-48"
                      disabled={isSubmitting}
                      toastSuccess={toast.success}
                      toastError={toast.error}
                      successMessage={t("org.edit.coverUploadSuccess")}
                      errorMessage={t("org.edit.coverUploadError")}
                      invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                      fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                      alt="Cover preview"
                    />
                  )}
                </form.Field>
                {/* Name */}
                <form.Field
                  name="name"
                  validators={{
                    onChange: ({ value }) => (!value?.trim() ? t("org.edit.errors.nameRequired") : undefined)
                  }}
                >
                  {(field) => (
                    <Input
                      label={t("org.edit.name")}
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t("org.edit.namePlaceholder")}
                      required
                      error={field.state.meta.errors[0] || undefined}
                      disabled={isSubmitting}
                    />
                  )}
                </form.Field>

                {/* Tagline */}
                <form.Field name="tagline">
                  {(field) => (
                    <Input
                      label={t("org.edit.tagline")}
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t("org.edit.taglinePlaceholder")}
                      error={field.state.meta.errors[0] || undefined}
                      disabled={isSubmitting}
                    />
                  )}
                </form.Field>

                {/* Description */}
                <form.Field
                  name="description"
                  validators={{
                    onChange: ({ value }) =>
                      !value?.trim() ? t("org.edit.errors.descriptionRequired") : undefined
                  }}
                >
                  {(field) => (
                    <CustomEditor
                      label={t("org.edit.description")}
                      required
                      error={field.state.meta.errors[0] || undefined}
                      value={field.state.value || ""}
                      onChangeCallback={field.handleChange}
                      disabled={isSubmitting}
                      config={{
                        toolbar: {
                          items: [
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "removeFormat",
                            "link",
                            "bulletedList",
                            "numberedList",
                            "|",
                            "blockQuote",
                            "|",
                            "undo",
                            "redo"
                          ],
                          shouldNotGroupWhenFull: false
                        }
                      }}
                    />
                  )}
                </form.Field>

                {/* Mission */}
                <form.Field name="mission">
                  {(field) => (
                    <CustomEditor
                      label={t("org.edit.mission")}
                      required
                      error={field.state.meta.errors[0] || undefined}
                      value={field.state.value || ""}
                      onChangeCallback={field.handleChange}
                      disabled={isSubmitting}
                      config={{
                        toolbar: {
                          items: [
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "removeFormat",
                            "link",
                            "bulletedList",
                            "numberedList",
                            "|",
                            "blockQuote",
                            "|",
                            "undo",
                            "redo"
                          ],
                          shouldNotGroupWhenFull: false
                        }
                      }}
                    />
                  )}
                </form.Field>
              </div>
            </Box>

            <div className="px-4 md:px-6 py-2">
              <p className="text-text-secondary text-responsive-lg font-normal">
                {t("org.edit.contactInfo")}
              </p>
            </div>
            <Box className="space-y-6 mb-6">
              {/* Contact Information Section */}
              <div className="space-y-4">
                {/* Email */}
                <form.Field
                  name="contact.email"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
                        return t("org.edit.errors.invalidEmail");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <Input
                      label={t("org.edit.email")}
                      type="email"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      placeholder="contact@example.com"
                      error={field.state.meta.errors[0] || undefined}
                      disabled={isSubmitting}
                    />
                  )}
                </form.Field>

                {/* Phone */}
                <form.Field name="contact.phone">
                  {(field) => (
                    <Input
                      label={t("org.edit.phone")}
                      type="tel"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      placeholder="+84 123 456 789"
                      error={field.state.meta.errors[0] || undefined}
                      disabled={isSubmitting}
                    />
                  )}
                </form.Field>

                {/* Website */}
                <form.Field
                  name="contact.website"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                        return t("org.edit.errors.invalidUrl");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <Input
                      label={t("org.edit.website")}
                      type="url"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      placeholder="https://example.com"
                      error={field.state.meta.errors[0] || undefined}
                      disabled={isSubmitting}
                    />
                  )}
                </form.Field>

                {/* Social Media */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Instagram */}
                  <form.Field
                    name="contact.instagram"
                    validators={{
                      onChange: ({ value }) => {
                        if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                          return t("org.edit.errors.invalidUrl");
                        }
                        return undefined;
                      }
                    }}
                  >
                    {(field) => (
                      <Input
                        label={t("org.edit.instagram")}
                        type="url"
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        placeholder="https://instagram.com/..."
                        error={field.state.meta.errors[0] || undefined}
                        disabled={isSubmitting}
                      />
                    )}
                  </form.Field>

                  {/* Facebook */}
                  <form.Field
                    name="contact.facebook"
                    validators={{
                      onChange: ({ value }) => {
                        if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                          return t("org.edit.errors.invalidUrl");
                        }
                        return undefined;
                      }
                    }}
                  >
                    {(field) => (
                      <Input
                        label={t("org.edit.facebook")}
                        type="url"
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        placeholder="https://facebook.com/..."
                        error={field.state.meta.errors[0] || undefined}
                        disabled={isSubmitting}
                      />
                    )}
                  </form.Field>

                  {/* Twitter */}
                  <form.Field
                    name="contact.twitter"
                    validators={{
                      onChange: ({ value }) => {
                        if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                          return t("org.edit.errors.invalidUrl");
                        }
                        return undefined;
                      }
                    }}
                  >
                    {(field) => (
                      <Input
                        label={t("org.edit.twitter")}
                        type="url"
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        placeholder="https://twitter.com/..."
                        error={field.state.meta.errors[0] || undefined}
                        disabled={isSubmitting}
                      />
                    )}
                  </form.Field>

                  {/* YouTube */}
                  <form.Field
                    name="contact.youtube"
                    validators={{
                      onChange: ({ value }) => {
                        if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                          return t("org.edit.errors.invalidUrl");
                        }
                        return undefined;
                      }
                    }}
                  >
                    {(field) => (
                      <Input
                        label={t("org.edit.youtube")}
                        type="url"
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        placeholder="https://youtube.com/..."
                        error={field.state.meta.errors[0] || undefined}
                        disabled={isSubmitting}
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </Box>
            {/* Submit Buttons */}
            <div className="flex gap-4 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                padding="px-12 py-3"
                onClick={() => navigate({ to: "/org/$orgId", params: { orgId: org.id } })}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <form.Subscribe selector={(state) => state.isDirty}>
                {(isDirty) => (
                  <Button
                    type="submit"
                    padding="px-12 py-3"
                    variant="default"
                    disabled={isSubmitting || !isDirty}
                  >
                    {isSubmitting ? t("common.saving") : t("common.save")}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </div>
      </div>
    </RawLayout>
  );
}
